import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import apiClient from '../../utils/api';

const EDITABLE_TYPES = ['text', 'image', 'photoGrid', 'photoPlaceholder', 'logoPlaceholder', 'headshotPlaceholder', 'rect', 'circle'];

export const useTemplateEngine = ({
  template,
  onElementsChange,
  availablePhotos = [],
  initialElements = [],
  initialBgColor = '#FFFFFF',
  photoAdjustments = null,
  onPhotoAdjustmentsChange = null
}) => {
  const stageRef = useRef();
  const fileInputRef = useRef();
  const saveTimerRef = useRef(null);
  // Track which (template_id) we've already auto-filled photos for, so we don't
  // overwrite the user's photo choices on subsequent re-renders. Reset when
  // template changes.
  const autoFilledForTemplate = useRef(null);

  // Photo picker state
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [photoPickerTarget, setPhotoPickerTarget] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);

  // Multi-sided (legacy)
  const isMultiSided = template?.canvas_data?.isMultiSided || false;
  const [currentSide, setCurrentSide] = useState('front');
  const [sidesData, setSidesData] = useState({
    front: { elements: [], backgroundColor: '#FFFFFF' },
    back: { elements: [], backgroundColor: '#FFFFFF' }
  });

  // Multi-page support
  const isMultiPage = template?.canvas_data?.isMultiPage || false;
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Core state
  const [elements, setElements] = useState(initialElements);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedGridSlot, setSelectedGridSlot] = useState(null);
  const [selectedPhotoPlaceholder, setSelectedPhotoPlaceholder] = useState(null);
  const [scale, setScale] = useState(0.5);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [inlineEditingId, setInlineEditingId] = useState(null);
  const [bgColor, setBgColor] = useState(initialBgColor);
  const [showGridPicker, setShowGridPicker] = useState(false);
  const [brandKit, setBrandKit] = useState(null);
  const [autoMarketingLoading, setAutoMarketingLoading] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [selectedFont, setSelectedFont] = useState('Playfair Display');

  // Undo/Redo history
  const historyRef = useRef([]);
  const redoStackRef = useRef([]);
  const MAX_HISTORY = 50;

  const pushHistory = useCallback(() => {
    setElements(prev => {
      historyRef.current = [...historyRef.current.slice(-(MAX_HISTORY - 1)), JSON.parse(JSON.stringify(prev))];
      redoStackRef.current = []; // Clear redo stack on new action
      return prev;
    });
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) {
      return;
    }
    setElements(current => {
      redoStackRef.current.push(JSON.parse(JSON.stringify(current)));
      return historyRef.current.pop();
    });
    setSelectedId(null);
    setSelectedGridSlot(null);
    setSelectedPhotoPlaceholder(null);
  }, []);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) {
      return;
    }
    setElements(current => {
      historyRef.current.push(JSON.parse(JSON.stringify(current)));
      return redoStackRef.current.pop();
    });
    setSelectedId(null);
    setSelectedGridSlot(null);
    setSelectedPhotoPlaceholder(null);
  }, []);

  const canvasWidth = template?.canvas_data?.width || 1080;
  const canvasHeight = template?.canvas_data?.height || 1080;

  // --- Brand Kit Color & Font Mapper ---
  const applyBrandKit = useCallback((el) => {
    const updated = { ...el };
    const id = (el.id || '').toLowerCase();
    const type = el.type || '';

    // --- Auto-fit text for single-line dynamic fields (runs even without brandKit) ---
    // Address, price, agent name etc. vary in length from listing to listing;
    // mark them as autoFit so they shrink to one line instead of wrapping.
    if (type === 'text' && updated.autoFit !== false) {
      const text = (updated.text || '').toLowerCase();
      const isDynamicField =
        id.includes('address') || id.includes('price') ||
        id.includes('agent-name') || id.includes('agentname') ||
        id.includes('headline') || id.includes('title') ||
        id.includes('brokerage') || id.includes('phone') ||
        text.includes('{{address}}') || text.includes('{{price}}') ||
        text.includes('{{agent_name}}') || text.includes('{{brokerage}}') ||
        text.includes('{{phone}}') || text.includes('{{headline}}');
      if (isDynamicField) {
        updated.autoFit = true;
        updated.fontSizeBase = updated.fontSizeBase || updated.fontSize || 24;
      }
    }

    if (!brandKit) return updated;

    // --- Color mapping ---
    if (brandKit.primary_color) {
      // Primary color → main backgrounds, header bars, CTA button fills
      if (type === 'rect' && (id.includes('bg') || id.includes('background') || id.includes('header') || id.includes('bar') || id.includes('cta'))) {
        // Only replace dark/colored fills, not white/light fills
        if (updated.fill && updated.fill !== '#ffffff' && updated.fill !== '#FFFFFF' && updated.fill !== 'transparent' && updated.fill !== '#E5E5E5') {
          updated.fill = brandKit.primary_color;
        }
      }
    }

    if (brandKit.accent_color && type === 'text') {
      // Accent color → price, badges, taglines, accent-colored text
      if (id.includes('price') || id.includes('badge') || id.includes('tagline') || id.includes('accent') || id.includes('cta-text')) {
        updated.fill = brandKit.accent_color;
      }
    }

    if (brandKit.secondary_color && type === 'text') {
      // Secondary color → agent info, details, subtitles
      if (id.includes('agent') || id.includes('detail') || id.includes('subtitle')) {
        updated.fill = brandKit.secondary_color;
      }
    }

    // --- Font mapping ---
    if (type === 'text') {
      const fontSize = el.fontSize || 14;
      const isBold = el.fontWeight === 'bold' || el.fontWeight === '700';
      // Primary font → headings (bold or large text ≥20px)
      if (brandKit.font_primary && (isBold || fontSize >= 20)) {
        updated.fontFamily = brandKit.font_primary;
      }
      // Secondary font → body text (non-bold, smaller text)
      if (brandKit.font_secondary && !isBold && fontSize < 20) {
        updated.fontFamily = brandKit.font_secondary;
      }
    }

    // --- Auto-fit text for single-line dynamic fields ---
    // Address, price, agent name etc. can vary in length from listing to listing;
    // mark them as autoFit so they shrink to one line instead of wrapping.
    if (type === 'text' && updated.autoFit !== false) {
      const text = (updated.text || '').toLowerCase();
      const isDynamicField =
        id.includes('address') || id.includes('price') ||
        id.includes('agent-name') || id.includes('agentname') ||
        id.includes('headline') || id.includes('title') ||
        id.includes('brokerage') || id.includes('phone') ||
        text.includes('{{address}}') || text.includes('{{price}}') ||
        text.includes('{{agent_name}}') || text.includes('{{brokerage}}') ||
        text.includes('{{phone}}') || text.includes('{{headline}}');
      if (isDynamicField) {
        updated.autoFit = true;
        updated.fontSizeBase = updated.fontSizeBase || updated.fontSize || 24;
      }
    }

    return updated;
  }, [brandKit]);

  // --- Effects ---

  // Load multi-page templates
  useEffect(() => {
    if (isMultiPage && template?.canvas_data?.pages) {
      const loadedPages = template.canvas_data.pages.map(page => {
        const mapped = (page.elements || []).map(el => {
          if (el.type === 'logoPlaceholder' && brandKit?.logo_url) {
            return applyBrandKit({ ...el, type: 'image', src: brandKit.logo_url, editable: false, isLogoFromBrandKit: true, originalType: 'logoPlaceholder' });
          }
          if (el.type === 'headshotPlaceholder' && brandKit?.headshot_url) {
            return applyBrandKit({ ...el, type: 'image', src: brandKit.headshot_url, editable: false, isHeadshotFromBrandKit: true, originalType: 'headshotPlaceholder' });
          }
          return applyBrandKit({
            ...el,
            editable: ['text', 'image', 'photoGrid', 'photoPlaceholder'].includes(el.type),
            isPlaceholder: el.type === 'image' && (!el.src || el.src.includes('placeholder'))
          });
        });
        return { ...page, elements: mapped };
      });
      setPages(loadedPages);
      setCurrentPageIndex(0);
      if (loadedPages.length > 0) {
        setElements(loadedPages[0].elements);
        setBgColor(loadedPages[0].backgroundColor || '#FFFFFF');
      }
      return;
    }
  }, [isMultiPage, template, brandKit, applyBrandKit]);

  const switchPage = useCallback((idx) => {
    if (!isMultiPage || idx === currentPageIndex) return;
    // Save current page
    setPages(prev => {
      const updated = [...prev];
      if (updated[currentPageIndex]) {
        updated[currentPageIndex] = { ...updated[currentPageIndex], elements, backgroundColor: bgColor };
      }
      return updated;
    });
    // Load new page
    setCurrentPageIndex(idx);
    const targetPage = pages[idx];
    if (targetPage) {
      setElements(targetPage.elements || []);
      setBgColor(targetPage.backgroundColor || '#FFFFFF');
    }
    setSelectedId(null);
  }, [isMultiPage, currentPageIndex, elements, bgColor, pages]);

  useEffect(() => {
    if (isMultiSided && template?.canvas_data?.sides) {
      setSidesData({
        front: template.canvas_data.sides.front || { elements: [], backgroundColor: '#FFFFFF' },
        back: template.canvas_data.sides.back || { elements: [], backgroundColor: '#FFFFFF' }
      });
      const frontElements = template.canvas_data.sides.front?.elements || [];
      setElements(frontElements.map(el => ({ ...el, editable: EDITABLE_TYPES.includes(el.type) })));
      setBgColor(template.canvas_data.sides.front?.backgroundColor || '#FFFFFF');
    }
  }, [template, isMultiSided]);

  useEffect(() => {
    const fetchBrandKit = async () => {
      try {
        const response = await apiClient.get('/profile/brand-kit');
        setBrandKit(response.data);
      } catch (err) {
        console.log('No brand kit found');
      }
    };
    fetchBrandKit();
  }, []);

  // Track whether we've already seeded the editor from a draft's saved
  // canvas state. When opening an existing draft, the parent passes the
  // saved elements via `initialElements` — we must NOT let the template-
  // defaults effect (below) clobber those with raw {{address}}/{{price}}
  // placeholders. This ref ensures the draft state wins exactly once on
  // mount and isn't reset when brandKit/photoAdjustments later load.
  const initialHydrationDone = useRef(false);

  useEffect(() => {
    if (isMultiPage) return; // handled by multi-page effect above

    // Seed from existing draft once — saved elements already contain
    // property data, photo placements and brand styling.
    if (
      initialElements && initialElements.length > 0 &&
      !initialHydrationDone.current
    ) {
      setElements(initialElements);
      setBgColor(initialBgColor || '#FFFFFF');
      initialHydrationDone.current = true;
      return;
    }
    // If we already hydrated from a draft, don't re-run template defaults
    // when brandKit / photoAdjustments arrive asynchronously.
    if (initialHydrationDone.current) return;

    if (template?.canvas_data) {
      const loadedElements = template.canvas_data.elements || [];
      const mapped = loadedElements.map(el => {
        if (el.type === 'logoPlaceholder' && brandKit?.logo_url) {
          return applyBrandKit({ ...el, type: 'image', src: brandKit.logo_url, editable: false, isLogoFromBrandKit: true, originalType: 'logoPlaceholder' });
        }
        if (el.type === 'headshotPlaceholder' && brandKit?.headshot_url) {
          return applyBrandKit({ ...el, type: 'image', src: brandKit.headshot_url, editable: false, isHeadshotFromBrandKit: true, originalType: 'headshotPlaceholder' });
        }
        return applyBrandKit({
          ...el,
          editable: ['text', 'image', 'photoGrid', 'photoPlaceholder'].includes(el.type),
          isPlaceholder: el.type === 'image' && (!el.src || el.src.includes('placeholder'))
        });
      });

      // Apply saved photo_adjustments if available
      if (photoAdjustments && typeof photoAdjustments === 'object') {
        mapped.forEach(el => {
          if (el.type === 'photoGrid' && el.slots) {
            el.slots = el.slots.map((s, i) => {
              const key = `${el.id}_slot_${i}`;
              const adj = photoAdjustments[key];
              if (adj) {
                return {
                  ...s,
                  src: adj.src || s.src,
                  imageZoom: adj.zoom ?? s.imageZoom ?? 1,
                  imageOffsetX: adj.offsetX ?? s.imageOffsetX ?? 0.5,
                  imageOffsetY: adj.offsetY ?? s.imageOffsetY ?? 0.5
                };
              }
              return s;
            });
          }
          if (el.type === 'image' && el.wasPhotoPlaceholder) {
            const adj = photoAdjustments[el.id];
            if (adj) {
              el.src = adj.src || el.src;
              el.imageZoom = adj.zoom ?? el.imageZoom ?? 1;
              el.imageOffsetX = adj.offsetX ?? el.imageOffsetX ?? 0.5;
              el.imageOffsetY = adj.offsetY ?? el.imageOffsetY ?? 0.5;
            }
          }
        });
      }

      setElements(mapped);
      setBgColor(template.canvas_data.backgroundColor || '#FFFFFF');
      initialHydrationDone.current = true;
    }
  }, [template, brandKit, photoAdjustments, applyBrandKit, initialElements, initialBgColor, isMultiPage]);

  // Auto-fill empty photo placeholders / grid slots with photos from the
  // active listing (scraper). Only runs ONCE per template — it never touches
  // a slot the user has already picked, and `wasPhotoPlaceholder`/auto-fit/
  // zoom/drag behaviour is preserved (the user can swap photos any time via
  // the existing photo picker or the "Replace" button).
  useEffect(() => {
    if (!template?.template_id) return;
    if (!availablePhotos || availablePhotos.length === 0) return;
    if (elements.length === 0) return;
    if (autoFilledForTemplate.current === template.template_id) return;

    let mutated = false;
    let photoCursor = 0;
    const nextPhoto = () => (photoCursor < availablePhotos.length ? availablePhotos[photoCursor++] : null);

    const next = elements.map(el => {
      // Empty photoPlaceholder → assign the next available photo
      if (el.type === 'photoPlaceholder' && !el.src && !el.autoFillSkip) {
        const photo = nextPhoto();
        if (photo) {
          mutated = true;
          return {
            ...el,
            type: 'image',
            src: photo,
            editable: true,
            wasPhotoPlaceholder: true,
            autoFilled: true,
            imageZoom: el.imageZoom ?? 1,
            imageOffsetX: el.imageOffsetX ?? 0.5,
            imageOffsetY: el.imageOffsetY ?? 0.5,
          };
        }
      }
      // Empty grid slots → fill in order, leave non-empty slots alone
      if (el.type === 'photoGrid' && Array.isArray(el.slots)) {
        let slotsMutated = false;
        const newSlots = el.slots.map(s => {
          if (s.src) return s;
          const photo = nextPhoto();
          if (!photo) return s;
          slotsMutated = true;
          return {
            ...s,
            src: photo,
            imageZoom: s.imageZoom ?? 1,
            imageOffsetX: s.imageOffsetX ?? 0.5,
            imageOffsetY: s.imageOffsetY ?? 0.5,
          };
        });
        if (slotsMutated) {
          mutated = true;
          return { ...el, slots: newSlots };
        }
      }
      return el;
    });

    if (mutated) {
      setElements(next);
    }
    autoFilledForTemplate.current = template.template_id;
  }, [template?.template_id, availablePhotos, elements]);

  useEffect(() => {
    if (onElementsChange) onElementsChange(elements, bgColor);
  }, [elements, bgColor, onElementsChange]);

  useEffect(() => {
    if (selectedId) {
      const el = elements.find(e => e.id === selectedId);
      setSelectedElement(el);
      if (el?.type === 'text') {
        setEditingText(el.text || '');
        setSelectedFont(el.fontFamily || 'Playfair Display');
      }
    } else {
      setSelectedElement(null);
      setEditingText('');
    }
  }, [selectedId, elements]);

  // --- Keyboard shortcuts (undo/redo + delete) ---
  // Clipboard / duplicate / layer-order / nudge shortcuts live in a second
  // effect below, after those handlers are declared.
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable)) return;

      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !(e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        pushHistory();
        setElements(prev => prev.filter(el => el.id !== selectedId));
        setSelectedId(null);
        setSelectedGridSlot(null);
        setSelectedPhotoPlaceholder(null);
        toast.success('Element deleted');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, selectedId, pushHistory]);

  // --- Core Handlers ---

  // Debounce timer so a flurry of small changes (drag, slider, typing) only
  // creates one undo entry per "interaction" instead of one per pixel.
  const updateDebounceRef = useRef({ timer: null, lastId: null, snapshotPushed: false });

  const updateElement = useCallback((updatedElement) => {
    setElements(prev => {
      // If this is the start of a new interaction (different element or no
      // pending debounce), snapshot the prior state for undo.
      const state = updateDebounceRef.current;
      if (!state.snapshotPushed || state.lastId !== updatedElement.id) {
        historyRef.current = [
          ...historyRef.current.slice(-(MAX_HISTORY - 1)),
          JSON.parse(JSON.stringify(prev)),
        ];
        redoStackRef.current = [];
        state.snapshotPushed = true;
        state.lastId = updatedElement.id;
      }

      // Reset the "pushed" flag after 600ms of inactivity so the next
      // distinct interaction creates a new history entry.
      if (state.timer) clearTimeout(state.timer);
      state.timer = setTimeout(() => {
        state.snapshotPushed = false;
        state.lastId = null;
      }, 600);

      return prev.map(el => el.id === updatedElement.id ? updatedElement : el);
    });
  }, []);

  // Extract photo_adjustments from all grid slots for persistence
  // NOTE: Defined early to avoid circular dependency with triggerAutoSave
  const getPhotoAdjustments = useCallback(() => {
    const adjustments = {};
    elements.forEach(el => {
      if (el.type === 'photoGrid' && el.slots) {
        el.slots.forEach((s, i) => {
          if (s.src && (s.imageZoom !== 1 || s.imageOffsetX !== 0.5 || s.imageOffsetY !== 0.5)) {
            adjustments[`${el.id}_slot_${i}`] = {
              src: s.src,
              zoom: s.imageZoom || 1,
              offsetX: s.imageOffsetX ?? 0.5,
              offsetY: s.imageOffsetY ?? 0.5
            };
          }
        });
      }
      if (el.type === 'image' && el.wasPhotoPlaceholder && el.src) {
        adjustments[el.id] = {
          src: el.src,
          zoom: el.imageZoom || 1,
          offsetX: el.imageOffsetX ?? 0.5,
          offsetY: el.imageOffsetY ?? 0.5
        };
      }
    });
    return adjustments;
  }, [elements]);

  // Debounced auto-save of photo adjustments
  // NOTE: Defined early to avoid circular dependency with handlers
  const triggerAutoSave = useCallback(() => {
    if (!onPhotoAdjustmentsChange) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const adj = getPhotoAdjustments();
      if (Object.keys(adj).length > 0) {
        onPhotoAdjustmentsChange(adj);
      }
    }, 1500);
  }, [onPhotoAdjustmentsChange, getPhotoAdjustments]);

  const deleteElement = useCallback((id) => {
    pushHistory();
    setElements(prev => prev.filter(el => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId, pushHistory]);

  // ── Part C: clipboard, duplicate, layer order ──
  const clipboardRef = useRef(null);

  const copyElement = useCallback((id) => {
    const el = elements.find(e => e.id === (id || selectedId));
    if (el) { clipboardRef.current = JSON.parse(JSON.stringify(el)); }
  }, [elements, selectedId]);

  const pasteElement = useCallback(() => {
    const src = clipboardRef.current;
    if (!src) return;
    pushHistory();
    const copy = { ...JSON.parse(JSON.stringify(src)), id: `${src.type || 'el'}-${Date.now()}`, x: (src.x || 0) + 20, y: (src.y || 0) + 20 };
    setElements(prev => [...prev, copy]);
    setSelectedId(copy.id);
  }, [pushHistory]);

  const duplicateElement = useCallback((id) => {
    const el = elements.find(e => e.id === (id || selectedId));
    if (!el) return;
    pushHistory();
    const copy = { ...JSON.parse(JSON.stringify(el)), id: `${el.type || 'el'}-${Date.now()}`, x: (el.x || 0) + 20, y: (el.y || 0) + 20 };
    setElements(prev => [...prev, copy]);
    setSelectedId(copy.id);
  }, [elements, selectedId, pushHistory]);

  const reorderLayer = useCallback((mode) => {
    if (!selectedId) return;
    pushHistory();
    setElements(prev => {
      const idx = prev.findIndex(e => e.id === selectedId);
      if (idx === -1) return prev;
      const isBg = (el) => el && (el.editable === false || (el.id && /(^|[-_])(bg|background|pattern)([-_]|$)/i.test(el.id)) || el.name === 'canvas-bg');
      // Floor = keep above any leading run of non-selectable background elements.
      let floor = 0;
      while (floor < prev.length && isBg(prev[floor])) floor++;
      const arr = [...prev];
      const [item] = arr.splice(idx, 1);
      const adjFloor = idx < floor ? Math.max(0, floor - 1) : floor;
      if (mode === 'front') arr.push(item);
      else if (mode === 'back') arr.splice(adjFloor, 0, item);
      else if (mode === 'forward') arr.splice(Math.min(arr.length, idx + 1), 0, item);
      else if (mode === 'backward') arr.splice(Math.max(adjFloor, idx - 1), 0, item);
      else arr.splice(idx, 0, item);
      return arr;
    });
  }, [selectedId, pushHistory]);

  // Nudge selected element by (dx,dy) canvas px — grouped into one undo burst
  // via updateElement's per-element debounce.
  const nudgeSelected = useCallback((dx, dy) => {
    if (!selectedId) return;
    const el = elements.find(e => e.id === selectedId);
    if (!el) return;
    updateElement({ ...el, x: (el.x || 0) + dx, y: (el.y || 0) + dy });
  }, [selectedId, elements, updateElement]);

  // Second shortcut layer: clipboard / duplicate / layer-order / arrow nudge.
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === 'c') { if (selectedId) { copyElement(); } return; }
      if (mod && e.key.toLowerCase() === 'v') { e.preventDefault(); pasteElement(); return; }
      if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); if (selectedId) duplicateElement(); return; }
      if (mod && (e.code === 'BracketRight' || e.key === ']' || e.key === '}')) { e.preventDefault(); reorderLayer(e.shiftKey ? 'front' : 'forward'); return; }
      if (mod && (e.code === 'BracketLeft' || e.key === '[' || e.key === '{')) { e.preventDefault(); reorderLayer(e.shiftKey ? 'back' : 'backward'); return; }

      if (!mod && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedId) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        if (e.key === 'ArrowUp') nudgeSelected(0, -step);
        else if (e.key === 'ArrowDown') nudgeSelected(0, step);
        else if (e.key === 'ArrowLeft') nudgeSelected(-step, 0);
        else if (e.key === 'ArrowRight') nudgeSelected(step, 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, copyElement, pasteElement, duplicateElement, reorderLayer, nudgeSelected]);

  const handleSelect = useCallback((id) => {
    setSelectedId(id);
    setSelectedGridSlot(null);
    setSelectedPhotoPlaceholder(null);
  }, []);

  const handleGridSlotSelect = useCallback((gridId, slotIndex) => {
    setSelectedId(gridId);
    setSelectedGridSlot({ gridId, slotIndex });
    setSelectedPhotoPlaceholder(null);
    // Only auto-open picker for empty slots
    const grid = elements.find(e => e.id === gridId);
    const slot = grid?.slots?.[slotIndex];
    if (!slot?.src && availablePhotos.length > 0) {
      setPhotoPickerTarget({ type: 'grid', gridId, slotIndex });
      setShowPhotoPicker(true);
    }
  }, [availablePhotos, elements]);

  const handlePhotoPlaceholderSelect = useCallback((id) => {
    setSelectedId(id);
    setSelectedPhotoPlaceholder(id);
    setSelectedGridSlot(null);
    if (availablePhotos.length > 0) {
      setPhotoPickerTarget({ type: 'placeholder', elementId: id });
      setShowPhotoPicker(true);
    }
  }, [availablePhotos]);

  const handleSelectFromAvailablePhotos = useCallback((photoUrl) => {
    pushHistory();
    if (photoPickerTarget?.type === 'placeholder') {
      const el = elements.find(e => e.id === photoPickerTarget.elementId);
      if (el) {
        updateElement({ ...el, type: 'image', src: photoUrl, editable: true, wasPhotoPlaceholder: true });
        setSelectedPhotoPlaceholder(null);
        toast.success('Property photo added!');
      }
    } else if (photoPickerTarget?.type === 'grid') {
      const grid = elements.find(e => e.id === photoPickerTarget.gridId);
      if (grid?.type === 'photoGrid') {
        const slots = [...grid.slots];
        slots[photoPickerTarget.slotIndex] = {
          ...slots[photoPickerTarget.slotIndex],
          src: photoUrl, imageOffsetX: 0.5, imageOffsetY: 0.5, imageZoom: 1
        };
        updateElement({ ...grid, slots });
        toast.success('Photo added to grid!');
      }
    } else if (photoPickerTarget?.type === 'image') {
      const el = elements.find(e => e.id === photoPickerTarget.elementId);
      if (el) {
        updateElement({ ...el, src: photoUrl });
        toast.success('Image replaced!');
      }
    }
    setShowPhotoPicker(false);
    setPhotoPickerTarget(null);
    triggerAutoSave();
  }, [photoPickerTarget, elements, updateElement, triggerAutoSave]);

  const handleDeselect = useCallback((e) => {
    const clickedOnStage = e.target === e.target.getStage();
    const clickedOnBg = e.target.name && e.target.name() === 'canvas-bg';
    if (clickedOnStage || clickedOnBg) {
      setSelectedId(null);
      setSelectedGridSlot(null);
      setSelectedPhotoPlaceholder(null);
    }
  }, []);

  // --- Text Handlers ---

  const handleTextChange = useCallback((newText) => {
    setEditingText(newText);
    if (selectedId && selectedElement?.type === 'text') {
      updateElement({ ...selectedElement, text: newText });
    }
  }, [selectedId, selectedElement, updateElement]);

  const handleFontChange = useCallback((newFont) => {
    setSelectedFont(newFont);
    if (selectedId && selectedElement?.type === 'text') {
      updateElement({ ...selectedElement, fontFamily: newFont });
    }
  }, [selectedId, selectedElement, updateElement]);

  // --- Image Upload ---

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    pushHistory();
    const applySrc = (src) => {
      if (selectedPhotoPlaceholder) {
        const el = elements.find(e2 => e2.id === selectedPhotoPlaceholder);
        if (el) {
          updateElement({ ...el, type: 'image', src, editable: true, wasPhotoPlaceholder: true });
          setSelectedPhotoPlaceholder(null);
          toast.success('Property photo added!');
        }
      } else if (selectedGridSlot) {
        const grid = elements.find(e2 => e2.id === selectedGridSlot.gridId);
        if (grid?.type === 'photoGrid') {
          const slots = [...grid.slots];
          slots[selectedGridSlot.slotIndex] = { ...slots[selectedGridSlot.slotIndex], src };
          updateElement({ ...grid, slots });
          toast.success('Photo added to grid!');
        }
      } else if (selectedId && selectedElement?.type === 'image') {
        updateElement({ ...selectedElement, src, isPlaceholder: false });
        toast.success('Image updated!');
      } else {
        setElements(prev => [...prev, {
          id: `user-image-${Date.now()}`, type: 'image', src,
          x: 100, y: 100, width: 300, height: 200,
          draggable: true, visible: true, editable: true
        }]);
        toast.success('Image added!');
      }
    };
    // Upload to storage and keep only the URL in the elements array — base64
    // data URLs bloat the undo history and the saved draft. Falls back to a
    // data URL only if the upload fails.
    const formData = new FormData();
    formData.append('file', file);
    const toastId = toast.loading('Uploading image…');
    apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        const rel = res.data?.url;
        if (!rel) throw new Error('no url');
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const url = rel.startsWith('http') ? rel : `${base}${rel}`;
        toast.dismiss(toastId);
        applySrc(url);
      })
      .catch(() => {
        const reader = new FileReader();
        reader.onload = (event) => { toast.dismiss(toastId); applySrc(event.target.result); };
        reader.readAsDataURL(file);
      });
    e.target.value = '';
  }, [selectedPhotoPlaceholder, selectedGridSlot, selectedId, selectedElement, elements, updateElement, pushHistory]);

  // --- Add Elements ---

  const addUserText = useCallback(() => {
    pushHistory();
    const el = {
      id: `user-text-${Date.now()}`, type: 'text', text: 'Your text here',
      x: 100, y: 100, fontSize: 32, fill: '#000000', fontFamily: selectedFont,
      draggable: true, visible: true, editable: true
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
  }, [selectedFont, pushHistory]);

  const addPhotoGrid = useCallback((gridConfig) => {
    pushHistory();
    const gridWidth = 400, gridHeight = 300, gap = 4;
    const slots = gridConfig.slots.map(s => ({
      x: 100 + s.x * gridWidth + (s.x > 0 ? gap : 0),
      y: 100 + s.y * gridHeight + (s.y > 0 ? gap : 0),
      width: s.w * gridWidth - (s.w < 1 ? gap : 0),
      height: s.h * gridHeight - (s.h < 1 ? gap : 0),
      src: null
    }));
    const el = {
      id: `photo-grid-${Date.now()}`, type: 'photoGrid', gridType: gridConfig.id,
      x: 100, y: 100, width: gridWidth, height: gridHeight,
      slots, draggable: true, visible: true, editable: true
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
    setShowGridPicker(false);
    toast.success(`${gridConfig.name} grid added!`);
  }, []);

  // ── Shape Helpers ──
  const addRect = useCallback((rounded = false) => {
    pushHistory();
    const el = {
      id: `rect-${Date.now()}`, type: 'rect',
      x: 100, y: 100, width: 200, height: 120,
      fill: '#8B5CF6', stroke: '', strokeWidth: 0,
      cornerRadius: rounded ? 16 : 0,
      opacity: 1, draggable: true, visible: true, editable: true,
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
    toast.success(rounded ? 'Rounded rectangle added' : 'Rectangle added');
  }, [pushHistory]);

  const addCircle = useCallback(() => {
    pushHistory();
    const el = {
      id: `circle-${Date.now()}`, type: 'circle',
      x: 200, y: 200, radius: 60,
      fill: '#EC4899', stroke: '', strokeWidth: 0,
      opacity: 1, draggable: true, visible: true, editable: true,
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
    toast.success('Circle added');
  }, [pushHistory]);

  // --- Side Switching ---

  const handleSideSwitch = useCallback((newSide) => {
    setSidesData(prev => ({
      ...prev,
      [currentSide]: { elements: [...elements], backgroundColor: bgColor }
    }));
    setCurrentSide(newSide);
    const sideElements = sidesData[newSide]?.elements || [];
    setElements(sideElements.map(el => ({ ...el, editable: EDITABLE_TYPES.includes(el.type) })));
    setBgColor(sidesData[newSide]?.backgroundColor || '#FFFFFF');
    setSelectedId(null);
  }, [currentSide, elements, bgColor, sidesData]);

  // --- Reset & Export ---

  const resetToOriginal = useCallback(() => {
    if (template?.canvas_data) {
      pushHistory();
      const loaded = template.canvas_data.elements || [];
      setElements(loaded.map(el => ({
        ...el,
        editable: ['text', 'image', 'photoGrid'].includes(el.type),
        isPlaceholder: el.type === 'image' && (!el.src || el.src.includes('placeholder'))
      })));
      setBgColor(template.canvas_data.backgroundColor || '#FFFFFF');
      setSelectedId(null);
      setSelectedGridSlot(null);
      toast.success('Template reset to original');
    }
  }, [template]);

  const downloadDesign = useCallback(() => {
    if (!stageRef.current) return;
    const prev = selectedId;
    setSelectedId(null);
    setTimeout(() => {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `realprop-design-${Date.now()}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSelectedId(prev);
      toast.success('Design saved to your device!');
    }, 100);
  }, [selectedId]);

  // --- AI Auto-Marketing ---

  const handleAutoMarketing = useCallback(async () => {
    const images = [];
    elements.forEach(el => {
      if (el.type === 'photoGrid' && el.slots) {
        el.slots.forEach(s => { if (s.src && !s.src.includes('placeholder')) images.push(s.src); });
      } else if (el.type === 'image' && el.src && !el.src.includes('placeholder')) {
        images.push(el.src);
      }
    });
    if (images.length === 0) { toast.error('Please add at least one property photo first'); return; }

    pushHistory();
    setAutoMarketingLoading(true);
    try {
      const response = await apiClient.post('/ai/auto-marketing', { images: images.slice(0, 4), style: 'catchy' });
      const el = {
        id: `auto-headline-${Date.now()}`, type: 'text', text: response.data.headline,
        x: canvasWidth / 2, y: 80, fontSize: 48, fontFamily: 'Playfair Display',
        fill: '#FFFFFF', align: 'center', width: canvasWidth - 100,
        offsetX: (canvasWidth - 100) / 2,
        shadowColor: 'rgba(0,0,0,0.8)', shadowBlur: 10, shadowOffsetX: 2, shadowOffsetY: 2,
        draggable: true, visible: true, editable: true
      };
      setElements(prev => [...prev, el]);
      setSelectedId(el.id);
      toast.success('AI headline generated!');
    } catch (err) {
      toast.error('Failed to generate headline.');
    } finally {
      setAutoMarketingLoading(false);
    }
  }, [elements, canvasWidth]);

  // --- Grid-specific Handlers ---

  const handleGridPanChange = useCallback((gridId, slotIndex, offsetX, offsetY) => {
    setElements(prev => prev.map(el => {
      if (el.id !== gridId) return el;
      const slots = el.slots.map((s, i) =>
        i === slotIndex ? { ...s, imageOffsetX: offsetX, imageOffsetY: offsetY } : s
      );
      return { ...el, slots };
    }));
    triggerAutoSave();
  }, [triggerAutoSave]);

  const handleGridZoomChange = useCallback((gridId, slotIndex, zoom) => {
    setElements(prev => prev.map(el => {
      if (el.id !== gridId) return el;
      const slots = el.slots.map((s, i) =>
        i === slotIndex ? { ...s, imageZoom: zoom } : s
      );
      return { ...el, slots };
    }));
    triggerAutoSave();
  }, [triggerAutoSave]);

  const handleGridDeleteImage = useCallback((gridId, slotIndex) => {
    pushHistory();
    setElements(prev => prev.map(el => {
      if (el.id !== gridId) return el;
      const slots = el.slots.map((s, i) =>
        i === slotIndex ? { ...s, src: null, imageOffsetX: 0.5, imageOffsetY: 0.5, imageZoom: 1 } : s
      );
      return { ...el, slots };
    }));
    toast.success('Photo removed');
  }, []);

  // --- Drop Handlers ---

  const findDragOverSlot = useCallback((canvasX, canvasY) => {
    let found = null;

    // Method 1: Konva getIntersection (pixel-perfect)
    const stage = stageRef.current;
    if (stage) {
      const shape = stage.getIntersection({ x: canvasX * scale, y: canvasY * scale });
      if (shape && shape.name && shape.name() === 'grid-slot-bg') {
        const gid = shape.getAttr('slotGridId');
        const si = shape.getAttr('slotIdx');
        if (gid !== undefined && si !== undefined) {
          found = { gridId: gid, slotIndex: si };
        }
      }
    }

    // Method 2: Coordinate matching fallback
    if (!found) {
      for (const el of elements) {
        if (el.type === 'photoGrid' && el.slots) {
          for (let i = 0; i < el.slots.length; i++) {
            const s = el.slots[i];
            if (canvasX >= s.x && canvasX <= s.x + s.width &&
                canvasY >= s.y && canvasY <= s.y + s.height) {
              found = { gridId: el.id, slotIndex: i };
              break;
            }
          }
        }
        if (found) break;
      }
    }

    setDragOverSlot(found);
  }, [elements, scale]);

  const handleDrop = useCallback((photoUrl, canvasX, canvasY) => {
    pushHistory();
    let dropped = false;

    // Method 1: Konva getIntersection
    const stage = stageRef.current;
    if (stage) {
      const shape = stage.getIntersection({ x: canvasX * scale, y: canvasY * scale });
      if (shape && shape.name && shape.name() === 'grid-slot-bg') {
        const gid = shape.getAttr('slotGridId');
        const si = shape.getAttr('slotIdx');
        if (gid !== undefined && si !== undefined) {
          const grid = elements.find(e => e.id === gid);
          if (grid?.type === 'photoGrid') {
            const slots = [...grid.slots];
            slots[si] = { ...slots[si], src: photoUrl, imageOffsetX: 0.5, imageOffsetY: 0.5, imageZoom: 1 };
            updateElement({ ...grid, slots });
            // Auto-select the slot so zoom/pan controls appear
            setSelectedId(gid);
            setSelectedGridSlot({ gridId: gid, slotIndex: si });
            setSelectedPhotoPlaceholder(null);
            toast.success(`Photo added to grid slot ${si + 1}!`);
            dropped = true;
          }
        }
      }
    }

    // Method 2: Coordinate matching fallback
    if (!dropped) {
      for (const el of elements) {
        if (el.type === 'photoGrid' && el.slots) {
          for (let i = 0; i < el.slots.length; i++) {
            const s = el.slots[i];
            if (canvasX >= s.x && canvasX <= s.x + s.width &&
                canvasY >= s.y && canvasY <= s.y + s.height) {
              const slots = [...el.slots];
              slots[i] = { ...slots[i], src: photoUrl, imageOffsetX: 0.5, imageOffsetY: 0.5, imageZoom: 1 };
              updateElement({ ...el, slots });
              // Auto-select the slot
              setSelectedId(el.id);
              setSelectedGridSlot({ gridId: el.id, slotIndex: i });
              setSelectedPhotoPlaceholder(null);
              toast.success(`Photo added to grid slot ${i + 1}!`);
              dropped = true;
              break;
            }
          }
        } else if (el.type === 'photoPlaceholder') {
          if (canvasX >= el.x && canvasX <= el.x + el.width &&
              canvasY >= el.y && canvasY <= el.y + el.height) {
            updateElement({ ...el, type: 'image', src: photoUrl, editable: true, wasPhotoPlaceholder: true });
            setSelectedPhotoPlaceholder(null);
            setSelectedId(el.id);
            toast.success('Property photo added!');
            dropped = true;
          }
        }
        if (dropped) break;
      }
    }

    if (!dropped) {
      const newId = `img_${Date.now()}`;
      setElements(prev => [...prev, {
        id: newId, type: 'image',
        x: Math.max(0, canvasX - 100), y: Math.max(0, canvasY - 75),
        width: 200, height: 150, src: photoUrl, editable: true, draggable: true,
        visible: true
      }]);
      // Auto-select the new image so user can immediately interact
      setSelectedId(newId);
      setSelectedGridSlot(null);
      setSelectedPhotoPlaceholder(null);
      toast.success('Image added to canvas!');
    }
    setDragOverSlot(null);
    triggerAutoSave();
  }, [elements, updateElement, scale, triggerAutoSave]);

  const clearDragOverSlot = useCallback(() => setDragOverSlot(null), []);

  // Hover tracking for overlay display (tracked via mouse position, not CSS hover)
  const [hoveredOverlay, setHoveredOverlay] = useState(null);

  const updateHoveredOverlay = useCallback((canvasX, canvasY) => {
    let found = null;
    for (const el of elements) {
      if (el.type === 'photoGrid' && el.slots) {
        for (let i = 0; i < el.slots.length; i++) {
          const s = el.slots[i];
          if (s.src && canvasX >= s.x && canvasX <= s.x + s.width &&
              canvasY >= s.y && canvasY <= s.y + s.height) {
            found = { type: 'gridSlot', gridId: el.id, slotIndex: i };
            break;
          }
        }
        if (found) break;
      }
      if (el.type === 'image' && el.src) {
        if (canvasX >= el.x && canvasX <= el.x + (el.width || 200) &&
            canvasY >= el.y && canvasY <= el.y + (el.height || 150)) {
          found = { type: 'image', id: el.id };
        }
      }
    }
    setHoveredOverlay(found);
  }, [elements]);

  const clearHoveredOverlay = useCallback(() => setHoveredOverlay(null), []);

  return {
    stageRef, fileInputRef,
    elements, selectedId, selectedElement, selectedGridSlot, selectedPhotoPlaceholder,
    scale, bgColor, showGridPicker, brandKit, autoMarketingLoading,
    stagePos, inlineEditingId,
    editingText, selectedFont, showPhotoPicker, photoPickerTarget, dragOverSlot,
    isMultiSided, currentSide, isMultiPage, pages, currentPageIndex, switchPage,
    canvasWidth, canvasHeight,
    setScale, setShowGridPicker, setShowPhotoPicker, setPhotoPickerTarget,
    setStagePos, setInlineEditingId,
    setSelectedId, setElements,
    handleSelect, handleGridSlotSelect, handlePhotoPlaceholderSelect,
    handleSelectFromAvailablePhotos, handleDeselect,
    updateElement, deleteElement, handleTextChange, handleFontChange,
    copyElement, pasteElement, duplicateElement, reorderLayer, nudgeSelected,
    handleImageUpload, addUserText, addPhotoGrid, addRect, addCircle,
    resetToOriginal, handleAutoMarketing, handleSideSwitch,
    downloadDesign, undo, redo, historyRef, redoStackRef,
    handleGridPanChange, handleGridZoomChange, handleGridDeleteImage,
    findDragOverSlot, handleDrop, clearDragOverSlot,
    hoveredOverlay, setHoveredOverlay, updateHoveredOverlay, clearHoveredOverlay,
    getPhotoAdjustments,
  };
};
